/* Ядро отправки итога «Сменить голос в записи» / «Почистить запись» по кнопке (deliver: on_request, 03-api.md §12.4) без React:
   `POST /app/orders/{id}/send` голосовым или файлом; форматы — только из заказа сервера (allowed_formats).
   Два способа отправки одной работы: отправленное голосовым не мешает отправить файлом.
   Ключ идемпотентности — на способ и ревизию результата (history-delivery-intent): неподтверждённый POST повторяется с прежним ключом.
   Одно ядро — один заказ: сведения, отметки отправки, ошибка и владелец принадлежат своему заказу.
   Его используют хук useVoiceDelivery (родной хвост) и экран движка (тот же код отправки). */
import { endpoints } from '../../api';
import type { HistoryOrder } from '../../api/history';
import { deliveryFormats } from '../history/history-model';
import { clearDeliveryIntent, deliveryIntent } from '../history/history-delivery-intent';
import { deliveryFormat, type VoiceOut } from './voice-model';

const OK = ['sent', 'delivered', 'pending', 'accepted', 'queued', 'sending'];
const LOAD_FAILED = 'Не удалось проверить отправку. Откройте работу в «Истории».';
const SEND_UNCONFIRMED = 'Отправка пока не подтверждена. Нажмите ещё раз — проверим тот же запрос.';

export interface VoiceDelivery {
  /** Сведения заказа и владелец: `GET /app/me` + `GET /app/history/{id}`; отменённое чтение ничего не меняет */
  load(signal: AbortSignal): Promise<void>;
  /** Сведения этого заказа загружены */
  ready(): boolean;
  /** Сведения этого заказа (null, пока не загружены) */
  detail(): HistoryOrder | null;
  /** Есть ли у заказа этот способ отправки */
  can(out: VoiceOut): boolean;
  /** Этот способ уже подтверждён сервером в этой странице */
  sent(out: VoiceOut): boolean;
  /** Отправка: владелец и ревизия результата сверяются заново; true — сервер принял отправку */
  send(out: VoiceOut): Promise<boolean>;
  error(): string;
  /** Способ, отправка которым идёт сейчас */
  busy(): VoiceOut | null;
}

export function createVoiceDelivery(orderId: number, o: { changed?(): void } = {}): VoiceDelivery {
  let loaded: HistoryOrder | null = null;
  let owner: number | null = null;
  let flight = false;
  let busy: VoiceOut | null = null;
  let error = '';
  let sent: Partial<Record<VoiceOut, boolean>> = {};
  const changed = () => o.changed?.();
  /* сведения относятся к своему заказу: ответ о другом заказе их не даёт */
  const detail = () => (loaded?.id === orderId ? loaded : null);
  const formats = () => {
    const value = detail();
    return value ? deliveryFormats(value) : [];
  };
  return {
    async load(signal) {
      if (!orderId) return;
      try {
        const [me, order] = await Promise.all([endpoints.me(), endpoints.history.get(orderId, signal)]);
        if (signal.aborted) return;
        owner = me.user.id;
        loaded = order;
        error = '';
        changed();
      } catch {
        if (signal.aborted) return;
        error = LOAD_FAILED;
        changed();
      }
    },
    ready: () => !!detail(),
    detail,
    can: out => !!deliveryFormat(formats(), out),
    sent: out => !!sent[out],
    async send(out) {
      const known = detail();
      if (!known || flight) return false;
      const format = deliveryFormat(formats(), out);
      if (!format) return false;
      flight = true;
      busy = out;
      error = '';
      changed();
      try {
        const me = await endpoints.me();
        if (me.user.id !== owner) throw new Error('owner_changed');
        const current = await endpoints.history.get(orderId);
        if (current.status !== 'done' || current.result?.available === false || current.result_revision !== known.result_revision)
          throw new Error('result_changed');
        const intent = deliveryIntent(me.user.id, orderId, current.result_revision ?? 1, format);
        const result = await endpoints.history.send(current, format, intent.key);
        const state = result.delivery?.state ?? '';
        if (['sent', 'delivered'].includes(state)) clearDeliveryIntent(intent);
        if (!OK.includes(state)) throw new Error('delivery_unconfirmed');
        loaded = { ...current, ...result };
        sent = { ...sent, [out]: true };
        return true;
      } catch {
        error = SEND_UNCONFIRMED;
        return false;
      } finally {
        flight = false;
        busy = null;
        changed();
      }
    },
    error: () => error,
    busy: () => busy,
  };
}
